import React from 'react';
import { motion } from "framer-motion";
import { Card, CardContent } from "@/shared/components/ui/card";
import { Button } from "@/shared/components/ui/button";
import { Mail, Phone, MapPin, MessageSquare, ArrowRight, Building2, HelpCircle, Loader2 } from "lucide-react";
import { contactService } from "@/shared/api/services/contactService";
import { useToast } from "@/shared/components/ui/use-toast";

const Contact = () => {
  const { toast } = useToast();
  const [isLoading, setIsLoading] = React.useState(false);
  const [formData, setFormData] = React.useState({
    fullName: '',
    email: '',
    message: ''
  });

  const handleChange = (e) => {
    const { id, value } = e.target;
    setFormData(prev => ({ ...prev, [id]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.fullName || !formData.email || !formData.message) {
      toast({
        title: "Missing Information",
        description: "Please fill in all fields before sending.",
        variant: "destructive"
      });
      return;
    }

    setIsLoading(true);
    try {
      await contactService.saveMessage(formData);
      toast({
        title: "Message Sent!",
        description: "Thank you for reaching out. We'll get back to you soon.",
      });
      setFormData({ fullName: '', email: '', message: '' });
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to send message. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="pt-32 pb-20 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Header Stream */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          className="text-center mb-20"
        >
          <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-slate-800/50 border border-slate-700/50 mb-8">
            <MessageSquare className="w-4 h-4 text-blue-400" />
            <span className="text-sm text-slate-400">Get in Touch</span>
          </div>
          <h1 className="text-4xl md:text-6xl font-bold mb-6">
            Let's build something <br />
            <span className="gradient-text">incredible together</span>
          </h1>
          <p className="text-lg text-slate-400 max-w-2xl mx-auto">
            Whether you have a question about our pricing, need technical support, or want to discuss a custom enterprise integration, our team is ready to help.
          </p>
        </motion.div>

        {/* Contact Grid */}
        <div className="grid lg:grid-cols-3 gap-8 mb-20">
          <motion.div
            className="lg:col-span-1 space-y-6"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            <h3 className="text-2xl font-bold text-white mb-6">Contact Information</h3>
            
            <Card className="bg-slate-900/50 border-slate-800/50 hover:border-slate-700 transition-colors">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-blue-500/10 flex items-center justify-center shrink-0">
                  <Mail className="h-5 w-5 text-blue-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Sales & Partnerships</h4>
                  <p className="text-slate-400 text-sm mb-2">Speak to our sales team about custom volume pricing.</p>
                  <a href="mailto:sales@luxkgate.com" className="text-blue-400 hover:text-blue-300 text-sm font-medium">sales@luxkgate.com</a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-800/50 hover:border-slate-700 transition-colors">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-emerald-500/10 flex items-center justify-center shrink-0">
                  <HelpCircle className="h-5 w-5 text-emerald-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Technical Support</h4>
                  <p className="text-slate-400 text-sm mb-2">Get help with API integrations and dashboard usage.</p>
                  <a href="mailto:support@luxkgate.com" className="text-emerald-400 hover:text-emerald-300 text-sm font-medium">support@luxkgate.com</a>
                </div>
              </CardContent>
            </Card>

            <Card className="bg-slate-900/50 border-slate-800/50 hover:border-slate-700 transition-colors">
              <CardContent className="p-6 flex items-start gap-4">
                <div className="w-10 h-10 rounded-lg bg-violet-500/10 flex items-center justify-center shrink-0">
                  <Building2 className="h-5 w-5 text-violet-400" />
                </div>
                <div>
                  <h4 className="text-white font-semibold mb-1">Global Headquarters</h4>
                  <p className="text-slate-400 text-sm leading-relaxed mb-2">
                    123 Financial District <br />
                    Suite 400 <br />
                    Toronto, ON M5V 2H1
                  </p>
                  <a href="#" className="text-violet-400 hover:text-violet-300 text-sm font-medium">Get Directions</a>
                </div>
              </CardContent>
            </Card>
          </motion.div>

          <motion.div
            className="lg:col-span-2"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            <Card className="bg-slate-900/80 border-slate-800 h-full relative overflow-hidden backdrop-blur-xl">
              <div className="absolute top-0 right-0 p-32 bg-blue-500/5 blur-[100px] rounded-full pointer-events-none" />
              <CardContent className="p-8 md:p-12 relative z-10">
                <h3 className="text-2xl font-bold text-white mb-8">Send us a message</h3>
                
                <form className="space-y-6" onSubmit={handleSubmit}>
                  <div className="space-y-2">
                    <label htmlFor="fullName" className="text-sm font-medium text-slate-300">Full Name</label>
                    <input 
                      type="text" 
                      id="fullName"
                      value={formData.fullName}
                      onChange={handleChange}
                      required
                      className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                      placeholder="John Doe"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="email" className="text-sm font-medium text-slate-300">Email Address</label>
                    <input 
                      type="email" 
                      id="email"
                      value={formData.email}
                      onChange={handleChange}
                      required
                      className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all"
                      placeholder="john@company.com"
                    />
                  </div>

                  <div className="space-y-2">
                    <label htmlFor="message" className="text-sm font-medium text-slate-300">Message</label>
                    <textarea 
                      id="message"
                      rows={5}
                      value={formData.message}
                      onChange={handleChange}
                      required
                      className="w-full bg-slate-950 border border-slate-800 rounded-lg px-4 py-3 text-white placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-blue-500/50 focus:border-blue-500/50 transition-all resize-none"
                      placeholder="Tell us a little bit about your current infrastructure and what you're looking to achieve..."
                    />
                  </div>

                  <Button 
                    type="submit"
                    disabled={isLoading}
                    className="w-full md:w-auto bg-gradient-to-r from-blue-500 to-violet-600 hover:from-blue-600 hover:to-violet-700 text-white px-8 py-6 text-base"
                  >
                    {isLoading ? (
                      <>
                        <Loader2 className="mr-2 h-5 w-5 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        Send Message
                        <ArrowRight className="ml-2 h-5 w-5" />
                      </>
                    )}
                  </Button>
                </form>

              </CardContent>
            </Card>
          </motion.div>
        </div>

      </div>
    </div>
  )
}

export default Contact;